import React from 'react';
import { Combobox } from 'evergreen-ui';
import { useDispatch, useSelector } from 'react-redux';
import { filteredStationsIdsSelector } from 'Features/Filters/Stations';
import { useLazyGetLastDataValuesQuery } from 'Services/api/reportsApi';
import { useGetAllMeteoCodesQuery } from 'Services/api/meteoCodesApi';
import { Request } from 'Features/StationsNew/Datasources/Datasource/Monitors/types';
import { setMapMeteocodeData } from './mapSlice';
import { useSelectedMapFilter } from './useSelectedMapFilter';

type MeteocodePart = {
  meteoCodeString?: string
}

export default function MapViewMeasureSelector() {
  const dispatch = useDispatch();
  const { selectedMapFilter } = useSelectedMapFilter();
  const filteredStationsIds = useSelector(filteredStationsIdsSelector);

  const [selectedMeteocode, setSelectedMeteocode] = React.useState<MeteocodePart>({});

  const [trigger] = useLazyGetLastDataValuesQuery();

  const isDisabled = React.useMemo(() => selectedMapFilter !== 'METEOCODE', [selectedMapFilter]);
  const { data: meteocodes = [], isFetching } = useGetAllMeteoCodesQuery(undefined, { skip: isDisabled });

  const sortedMeteocodes = React.useMemo(() => [...meteocodes].sort((a, b) => (a.name > b.name ? 1 : -1)), [meteocodes]);

  const onChange = (element: MeteocodePart) => {
    setSelectedMeteocode(element);
  };

  const getValues = React.useCallback(async (request: Request) => {
    const result = await trigger(request).unwrap();
    dispatch(setMapMeteocodeData(result));
  }, [dispatch, trigger]);

  React.useEffect(() => {
    if (selectedMeteocode?.meteoCodeString && filteredStationsIds.length > 0) {
      const request: Request = {
        calc: true,
        round: true,
        requestItems: filteredStationsIds.map((stationId) => ({
          meteoCode: selectedMeteocode.meteoCodeString as string,
          sourceId: stationId,
          sourceType: 'STATION',
          stationEntityId: -1,
          place: '',
          point: -1,
          time: null,
          measures: [{ interval: 10, timeShift: 0, mandatory: false }],
        })),
      };
      getValues(request);
    }
  }, [filteredStationsIds, getValues, selectedMeteocode]);

  return (
    <Combobox
      disabled={isDisabled || isFetching}
      width="100%"
      items={sortedMeteocodes}
      placeholder="Выберите метеокод"
      itemToString={(item) => (item ? `[${item.meteoCodeString}] ${item.name}` : '')}
      autocompleteProps={{
        popoverMinWidth: 800,
      } as never}
      onChange={onChange}
    />
  );
}
